/* ANL-03 / E16.1 — camada de dados do tema.
 *
 * O que este arquivo É: o tradutor entre o que o visitante faz no site e os
 * eventos que o Custom Pixel recebe. Ele NÃO carrega tag nenhuma. GA4, Meta e
 * TikTok vivem no Custom Pixel do admin, porque só ele enxerga o checkout e a
 * página de obrigado — a Shopify encerrou os scripts dessas páginas. Tag colada
 * aqui produziria um funil que morre em `begin_checkout` e nunca vê a compra.
 *
 * O que este arquivo NÃO faz, de propósito:
 *
 * - **Não reimplementa evento padrão.** `view_item`, `add_to_cart`,
 *   `begin_checkout`, `purchase` e `search_submit` são emitidos pela própria
 *   Shopify para o pixel. Publicá-los de novo daqui contaria o funil duas
 *   vezes. Só sai daqui o que a Shopify não vê.
 * - **Não carrega dado pessoal.** Nome, e-mail e telefone nunca entram em
 *   payload. O modal de lead manda interesse e perfil; quem guarda os dados da
 *   pessoa é o cadastro de cliente, não a telemetria.
 * - **Não usa classe de CSS como gancho.** Tudo resolve por `data-cof-action`,
 *   que é o contrato de `docs/product/BLUEPRINT-SELECTORS.json`. Classe é
 *   apresentação e muda numa refatoração de estilo; quando isso acontece com
 *   analytics acoplado, ele morre calado e ninguém percebe por semanas.
 *
 * ⚠️ SOBRE A FILA, PARA NÃO SER REMOVIDA POR ENGANO.
 * A ANL-01 mediu `Shopify.analytics.publish` disponível em 6 de 6 visitas —
 * ou seja, a fila NÃO existe porque a API costuma faltar. Ela existe pela
 * corrida de carregamento: este script pode rodar antes de a API aparecer, e
 * um clique nesse intervalo se perderia. Passado o teto, ela desiste em
 * silêncio: telemetria que lança erro, que fica em `setInterval` para sempre ou
 * que trava a navegação derruba a loja para medir a loja.
 *
 * A fábrica `criarMedidor` é pura e recebe tudo o que toca o mundo
 * (`publish`, `agora`, `agendar`, `consentiu`). É isso que deixa o teste
 * EXECUTAR os três casos difíceis — publish que nunca chega, que chega tarde e
 * que lança — em vez de procurar strings no fonte.
 */
(function (global) {
  'use strict';

  var PREFIXO = 'cofcof:';
  var TETO_FILA_MS = 5000;

  /* Ação do DOM → evento da taxonomia (§ 11 do blueprint).
   *
   * As chaves têm de existir em `futureActions` do BLUEPRINT-SELECTORS.json;
   * `TEST-ANL-TAXONOMIA` confere contra o JSON, não contra este arquivo, para
   * não acabar comparando código com código. */
  var MAPA = {
    'open-menu': 'nav_open',
    'nav-select': 'nav_select',
    'open-search': 'search_open',
    'open-account': 'account_open',
    'open-cart': 'view_cart_intent',
    'select-item': 'select_item',
    'select-intent': 'select_intent',
    'select-product-option': 'select_product_option',
    'select-purchase-mode': 'select_purchase_mode',
    'select-selling-plan': 'select_selling_plan',
    'apply-filters': 'filters_apply',
    'toggle-faq': 'faq_open',
    'play-video': 'video_start',
    'estimate-shipping': 'shipping_estimate',
    'newsletter-submit': 'newsletter_submit',
    'open-whatsapp-lead': 'whatsapp_intent',
    'lead-step': 'lead_step',
    'lead-option': 'lead_option',
    'whatsapp-lead-submit': 'generate_lead',
    'selector-start': 'selector_start',
    'selector-option': 'selector_option',
    'selector-complete': 'selector_complete',
    'partner-direction': 'partner_direction_click',
    'announcement-click': 'announcement_click',
    'remove-from-cart': 'remove_from_cart_intent',
    'begin-checkout': 'begin_checkout_intent',

    /* Ações que já existiam no tema antes de haver registro (ANL-04). Foram
     * mantidas com o nome delas — renomear 13 componentes em produção é risco
     * sem ganho — e mapeadas para o vocabulário do blueprint.
     *
     * ⚠️ `view-cart` e `open-cart` são SINÔNIMOS no HTML e convergem para o
     * mesmo evento de propósito: dois nomes para o mesmo ato contariam a mesma
     * intenção duas vezes no relatório.
     *
     * Ficam de fora do mapa, por não terem valor de funil: `close-menu`,
     * `back-to-partner-map` e `submit-search` — este último porque a busca já é
     * evento padrão da Shopify. */
    'view-cart': 'view_cart_intent',
    'hero-primary': 'cta_click',
    'hero-secondary': 'cta_click',
    'view-featured-collection': 'select_item',
    'view-story-product': 'select_item',
    'view-sold-out-product': 'select_item',
    'view-product-media': 'select_item',
    'view-origin-page': 'select_item',
    'add-product': 'select_item',
    'select-partner': 'partner_select',
  };

  /* Eventos que pertencem à Shopify. Se algum aparecer no MAPA, é defeito:
   * significa que o tema passou a contar de novo o que o pixel já conta. */
  var DA_SHOPIFY = [
    'page_viewed', 'product_viewed', 'collection_viewed', 'search_submitted',
    'product_added_to_cart', 'cart_viewed', 'checkout_started', 'checkout_completed',
    'view_item', 'add_to_cart', 'begin_checkout', 'purchase', 'search_submit',
  ];

  /* Chaves que podem viajar num payload. Allowlist, não bloqueio: campo novo
   * precisa ser autorizado aqui de propósito, e é isso que impede alguém de
   * anexar e-mail "só para facilitar" num evento de lead. */
  var CAMPOS_PERMITIDOS = [
    'acao', 'evento', 'superficie', 'valor', 'posicao', 'etapa', 'interesse',
    'perfil', 'id_produto', 'id_variante', 'plano', 'colecao', 'consulta',
    'pagina', 'template', 'video', 'parceiro', 'moeda', 'preco',
  ];

  var CAMPOS_PROIBIDOS = /(^|_)(email|e_mail|nome|name|telefone|phone|whatsapp|cpf|cnpj|endereco|address|senha|password|token)($|_)/i;

  function ehObjeto(v) { return v !== null && typeof v === 'object' && !Array.isArray(v); }

  /* Deixa passar só o que é permitido, e recusa qualquer coisa com cara de
   * dado pessoal mesmo que alguém a inclua na allowlist por engano. */
  function limpar(dados) {
    var saida = {};
    if (!ehObjeto(dados)) return saida;
    for (var k in dados) {
      if (!Object.prototype.hasOwnProperty.call(dados, k)) continue;
      if (CAMPOS_PROIBIDOS.test(k)) continue;
      if (CAMPOS_PERMITIDOS.indexOf(k) === -1) continue;
      var v = dados[k];
      if (v === null || v === undefined || v === '') continue;
      if (typeof v === 'object') continue;
      saida[k] = typeof v === 'string' ? String(v).slice(0, 120) : v;
    }
    return saida;
  }

  /**
   * @param {object} dep
   * @param {function} dep.publish   quem entrega ao pixel; pode faltar no começo
   * @param {function} dep.obterPublish  relê a API a cada tentativa (a corrida)
   * @param {function} dep.agora    relógio, em ms
   * @param {function} dep.agendar  agendador (setTimeout injetável)
   * @param {function} dep.consentiu devolve boolean; consultado a CADA evento
   * @param {function} [dep.aoErro] canal de erro para o teste; nunca lança
   */
  function criarMedidor(dep) {
    var fila = [];
    var nascido = dep.agora();
    var drenando = false;
    var desistiu = false;

    function entregar(nome, dados) {
      var publish = dep.obterPublish ? dep.obterPublish() : dep.publish;
      if (typeof publish !== 'function') return false;
      try {
        publish(PREFIXO + nome, dados);
        return true;
      } catch (e) {
        /* Pixel que lança não pode derrubar a página nem a fila. */
        if (dep.aoErro) dep.aoErro(e);
        return true;
      }
    }

    function drenar() {
      if (drenando || desistiu) return;
      drenando = true;
      var passo = function () {
        drenando = false;
        if (!fila.length) return;
        var publish = dep.obterPublish ? dep.obterPublish() : dep.publish;
        if (typeof publish === 'function') {
          while (fila.length) { var m = fila.shift(); entregar(m[0], m[1]); }
          return;
        }
        if (dep.agora() - nascido >= TETO_FILA_MS) {
          /* Desiste de vez: some com a fila e não agenda mais nada. */
          desistiu = true;
          fila.length = 0;
          return;
        }
        drenando = true;
        dep.agendar(passo, 150);
      };
      dep.agendar(passo, 150);
    }

    return {
      /* Publica um evento da taxonomia. Devolve o que foi publicado (ou null),
       * para o teste afirmar o payload em vez de afirmar a intenção. */
      publicar: function (nome, dados) {
        if (typeof nome !== 'string' || !nome) return null;
        if (DA_SHOPIFY.indexOf(nome) !== -1) {
          /* Guarda de fato: o evento da Shopify não sai daqui nem se alguém
           * chamar à mão. Contar duas vezes é pior que não contar. */
          if (dep.aoErro) dep.aoErro(new Error('evento da Shopify recusado: ' + nome));
          return null;
        }
        if (typeof dep.consentiu === 'function' && !dep.consentiu()) return null;

        var limpo = limpar(dados);
        if (entregar(nome, limpo)) return { nome: PREFIXO + nome, dados: limpo };
        if (desistiu) return null;
        fila.push([nome, limpo]);
        drenar();
        return { nome: PREFIXO + nome, dados: limpo, enfileirado: true };
      },

      /* Traduz um elemento com `data-cof-action` para um evento. */
      publicarDeElemento: function (el) {
        if (!el || !el.getAttribute) return null;
        var acao = el.getAttribute('data-cof-action');
        if (!acao || !MAPA[acao]) return null;
        var dados = { acao: acao };
        var sup = el.getAttribute('data-cof-surface');
        if (sup) dados.superficie = sup;
        var val = el.getAttribute('data-cof-value');
        if (val) dados.valor = val;
        return this.publicar(MAPA[acao], dados);
      },

      pendentes: function () { return fila.length; },
      desistiu: function () { return desistiu; },
    };
  }

  var API = { criarMedidor: criarMedidor, MAPA: MAPA, DA_SHOPIFY: DA_SHOPIFY, CAMPOS_PERMITIDOS: CAMPOS_PERMITIDOS, limpar: limpar, TETO_FILA_MS: TETO_FILA_MS };

  /* Exposto para o teste conseguir carregar o arquivo em `node:vm` e EXECUTAR
   * a lógica, em vez de procurar strings no fonte. */
  global.CofCofAnalytics = API;

  /* --- Ligação com a página. Só roda em navegador de verdade. --- */
  if (typeof document === 'undefined') return;

  function ilha() {
    var el = document.getElementById('cofcof-analytics-contexto');
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return {}; }
  }

  function iniciar() {
    var ctx = ilha();
    if (!ctx.ligado) return;

    var medidor = criarMedidor({
      obterPublish: function () {
        return global.Shopify && global.Shopify.analytics && global.Shopify.analytics.publish
          ? global.Shopify.analytics.publish.bind(global.Shopify.analytics)
          : null;
      },
      agora: function () { return Date.now(); },
      agendar: function (fn, ms) { return setTimeout(fn, ms); },
      /* Consultado a cada evento, e não uma vez no início: o visitante pode
       * recusar no meio da sessão, e um gate lido só no boot continuaria
       * medindo depois da recusa. */
      consentiu: function () {
        var cp = global.Shopify && global.Shopify.customerPrivacy;
        if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return false;
        try { return cp.analyticsProcessingAllowed(); } catch (e) { return false; }
      },
    });

    global.CofCofAnalytics.medidor = medidor;

    /* Um ouvinte só, delegado, na fase de captura: sobrevive a `stopPropagation`
     * de componente e não multiplica listener por elemento. */
    document.addEventListener('click', function (ev) {
      var alvo = ev.target && ev.target.closest ? ev.target.closest('[data-cof-action]') : null;
      if (alvo) medidor.publicarDeElemento(alvo);
    }, true);

    /* Vídeo não emite clique: o sinal é o `play`, e ele borbulha só em captura. */
    document.addEventListener('play', function (ev) {
      var v = ev.target;
      if (!v || v.tagName !== 'VIDEO') return;
      medidor.publicar('video_start', {
        video: v.getAttribute('data-cof-video-id') || v.getAttribute('poster') || 'sem-id',
      });
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }
})(typeof window !== 'undefined' ? window : globalThis);
